import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { listPayload, objectPayload } from '@/lib/payload';
import { foldToOther, sortStatusRows } from '@/lib/chartPalette';
import { completeMonths } from '@/lib/reportPeriods';
import { getApiErrorMessage } from '@/stores/partnerAuthStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusPieChart } from '@/components/charts/StatusPieChart';
import { MonthlyBarChart } from '@/components/charts/MonthlyBarChart';

type TierRow = {
  tier: string;
  isFree: boolean;
  count: number;
};

type NewVsChurnedRow = {
  month: string;
  new: number;
  churned: number;
  basis: 'exact' | 'approximated';
};

export function SubscriptionsPage() {
  const [tierMix, setTierMix] = useState<TierRow[]>([]);
  const [statusMix, setStatusMix] = useState<Array<{ status: string; count: number }>>([]);
  const [newVsChurned, setNewVsChurned] = useState<NewVsChurnedRow[]>([]);
  const [trialConversion, setTrialConversion] = useState<{ converted: number; notConverted: number; rate: number | null } | null>(null);

  useEffect(() => {
    // One toast id for the whole page so a shared failure raises one toast.
    const reportFailed = (error: unknown) =>
      toast.error(getApiErrorMessage(error), { id: 'subscriptions-report-error' });

    void api.get('/reports/subscriptions/tier-mix').then((res) => setTierMix(listPayload(res))).catch((error) => { setTierMix([]); reportFailed(error); });
    void api.get('/reports/subscriptions/status-mix').then((res) => setStatusMix(listPayload(res))).catch((error) => { setStatusMix([]); reportFailed(error); });
    void api.get('/reports/subscriptions/new-vs-churned?months=12').then((res) => setNewVsChurned(listPayload(res))).catch((error) => { setNewVsChurned([]); reportFailed(error); });
    void api.get('/reports/subscriptions/trial-conversion').then((res) => setTrialConversion(objectPayload(res))).catch((error) => { setTrialConversion(null); reportFailed(error); });
  }, []);

  const freeVsPaid = [
    { label: 'Free', count: tierMix.filter((t) => t.isFree).reduce((sum, t) => sum + t.count, 0) },
    { label: 'Paid', count: tierMix.filter((t) => !t.isFree).reduce((sum, t) => sum + t.count, 0) },
  ];

  // Pricing tiers are configured in the back office, so the tier count can
  // exceed the four identity hues at any time. Overflow folds into one
  // "Other" slice instead of several indistinguishable grey ones. Not applied
  // to freeVsPaid (always two) or to the status pies (a status is never
  // "Other").
  const tierMixData = foldToOther(tierMix, 'tier', 'count');

  /**
   * The API series ends on the current, still-accumulating month, whose
   * arrivals and departures are only those so far. Charting it puts a dip at
   * the right-hand edge of both bars every month, which is how an earlier card
   * on this portal showed a -61% month-over-month drop on flat growth.
   */
  const completedMonths = completeMonths(newVsChurned);

  // Both series are reconstructed from subscription timestamps rather than
  // read from a status history, which does not exist. Say so when any charted
  // month rests on that reconstruction.
  const isApproximated = completedMonths.some((row) => row.basis === 'approximated');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold">Subscriptions</h1>
        <p className="text-muted-foreground">Plan mix, status and churn across all groups.</p>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Groups by pricing tier</CardTitle>
          </CardHeader>
          <CardContent>
            <StatusPieChart data={tierMixData} nameKey="tier" valueKey="count" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Free vs paid</CardTitle>
          </CardHeader>
          <CardContent>
            <StatusPieChart data={freeVsPaid} nameKey="label" valueKey="count" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Subscription status</CardTitle>
          </CardHeader>
          <CardContent>
            <StatusPieChart data={sortStatusRows(statusMix, 'status')} nameKey="status" valueKey="count" variant="status" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>New vs churned</CardTitle>
          </CardHeader>
          <CardContent>
            <MonthlyBarChart
              data={completedMonths}
              series={[
                { key: 'new', label: 'New' },
                { key: 'churned', label: 'Churned' },
              ]}
            />
            <p className="text-xs text-muted-foreground">
              Groups that started or stopped paying, counted at each month end. Plan changes are not counted.
            </p>
            {isApproximated && (
              <p className="mt-1 text-xs text-muted-foreground">
                Reconstructed from subscription records. Figures for past months are approximate.
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Trial-to-paid conversion</CardTitle>
          </CardHeader>
          <CardContent>
            {trialConversion ? (
              <div className="space-y-2 text-sm">
                <p>Converted: {trialConversion.converted}</p>
                <p>Not converted: {trialConversion.notConverted}</p>
                <p>Rate: {trialConversion.rate !== null ? `${(trialConversion.rate * 100).toFixed(1)}%` : 'No expired trials yet'}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Loading...</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
